import React, { useState } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  Button,
  Typography,
  Box,
  styled
} from '@mui/material';
import { clearListeningCache } from '../../api/contests';
import { API_URL } from '../../app/constants'

const StyledSelect = styled(Select)({
  width: '100%',
  marginBottom: '16px',
  borderRadius: '4px !important',
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px !important',
    backgroundColor: '#FFFFFF',
    transition: 'all 0.2s',
    '& fieldset': {
      border: '1px solid #E5E7EB',
      borderRadius: '4px !important',
    },
    '&:hover': {
      backgroundColor: '#FFFFFF',
      '& fieldset': {
        borderColor: '#D1D5DB',
        borderRadius: '4px !important',
      },
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      boxShadow: '0 0 0 2px rgba(147, 51, 234, 0.1)',
      '& fieldset': {
        borderColor: '#9333EA',
        borderWidth: '1px',
        borderRadius: '4px !important',
      },
    },
  },
  '& .MuiSelect-select': {
    padding: '12px 14px',
    fontSize: '15px',
    fontFamily: 'Manrope, sans-serif',
    minHeight: '16px',
  },
  '& .MuiInputBase-root': {
    borderRadius: '4px !important',
  },
});

const FieldTitle = styled(Typography)({
  textAlign: 'left',
  marginBottom: '4px',
  fontWeight: '500',
  fontSize: '14px',
  color: '#374151',
  fontFamily: 'Manrope, sans-serif',
});

const ContinueButton = styled(Button)(({ disabled }) => ({
  width: '100%',
  marginTop: '1rem',
  marginBottom: '1rem',
  backgroundColor: disabled ? '#E9D5FF' : '#9333EA',
  color: 'white',
  borderRadius: '4px !important',
  border: '1px solid',
  borderColor: disabled ? '#E9D5FF' : '#9333EA',
  padding: '12px 14px',
  fontWeight: 600,
  fontSize: '15px',
  textTransform: 'none',
  letterSpacing: 0,
  fontFamily: 'Manrope, sans-serif',
  minHeight: '48px',
  boxShadow: 'none !important',
  '&.MuiButton-root': {
    borderRadius: '4px !important',
  },
  '&:hover': {
    backgroundColor: disabled ? '#E9D5FF' : '#7928CA',
    border: '1px solid',
    borderColor: disabled ? '#E9D5FF' : '#7928CA',
    boxShadow: 'none !important',
    borderRadius: '4px !important',
  },
  '&.Mui-disabled': {
    backgroundColor: '#E9D5FF',
    color: '#fff',
    border: '1px solid #E9D5FF',
    borderRadius: '4px !important',
  },
}));

const SelectTargetLevelModal = ({ onLevelSelect }) => {
  const [selectedLevel, setSelectedLevel] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLevelChange = (event) => {
    setSelectedLevel(event.target.value);
  };

  const handleLevelSelect = async () => {
    if (!selectedLevel) return;
    
    setIsLoading(true);
    try {
      const jwt = localStorage.getItem('jwt');
      const response = await fetch(`${API_URL}/api/v1/auth/change-level`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ level: selectedLevel })
      });

      if (response.ok) {
        // Clear listening cache when level changes to force refresh
        clearListeningCache();
        onLevelSelect(selectedLevel);
      } else {
        console.error('Failed to update level');
      }
    } catch (error) {
      console.error('Error updating level:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)'
      }}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '4px',
          padding: '32px',
          maxWidth: '400px',
          width: '90%',
          fontFamily: 'Manrope, sans-serif',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography 
          component="h1" 
          variant="h4" 
          sx={{ 
            textAlign: 'center',
            fontWeight: 700, 
            fontSize: '1.8rem', 
            color: '#111827',
            marginBottom: '16px',
            fontFamily: 'Manrope, sans-serif'
          }}
        >
          Select Your Target Level
        </Typography>
        
        <Box sx={{ textAlign: 'center', marginBottom: '32px' }}>
          <Typography 
            sx={{ 
              color: '#64748b',
              fontSize: '1rem',
              fontFamily: 'Manrope, sans-serif'
            }}
          >
            Choose the JLPT level you want to achieve
          </Typography>
        </Box>

        <Box>
          <FieldTitle>What's your target level?</FieldTitle>
          <FormControl fullWidth>
            <StyledSelect
              value={selectedLevel}
              onChange={handleLevelChange}
              displayEmpty
              MenuProps={{
                disablePortal: true,
                PaperProps: {
                  style: {
                    zIndex: 10001,
                    maxHeight: 200,
                  },
                },
                MenuListProps: {
                  style: {
                    zIndex: 10001,
                  },
                },
              }}
            >
              <MenuItem value="" disabled>
                Select your target level
              </MenuItem>
              <MenuItem value="N5">Level N5</MenuItem>
              <MenuItem value="N4">Level N4</MenuItem>
              <MenuItem value="N3">Level N3</MenuItem>
              <MenuItem value="N2">Level N2</MenuItem>
              <MenuItem value="N1">Level N1</MenuItem>
            </StyledSelect>
          </FormControl>
        </Box>

        <ContinueButton
          onClick={handleLevelSelect}
          disabled={!selectedLevel || isLoading}
          disableElevation
        >
          {isLoading ? 'Updating...' : 'Confirm Level'}
        </ContinueButton>
      </div>
    </div>
  );
};

export default SelectTargetLevelModal;